/**
 * 紧凑视图 - 时间列定义
 * 包含 STD/STA/ETD/ETA/ATD/ATA 六个独立时间列和详情按钮列
 */

import React from "react";
import { Space, Tag, Tooltip } from "antd";
import { InfoCircleOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import FlightTimeDisplay from "../../../../../../components/DateTimeDisplay";
import EnhancedFlightTimes from "../../EnhancedFlightTimes";
import { resolveTime } from "../../../../../../utils/resolveTime";

/**
 * 计算时间差异（分钟）
 */
const calculateTimeDiff = (time1, time2) => {
    if (!time1 || !time2) return null;
    return dayjs(time1).diff(dayjs(time2), "minute");
};

/**
 * 获取延误标签颜色
 */
const getDelayTagColor = (diff) => {
    if (!diff) return "default";
    if (diff <= 0) return "success";
    if (diff <= 15) return "default";
    if (diff <= 30) return "warning";
    return "error";
};

/**
 * 获取时间列定义
 * @param {Object} options - 配置选项
 * @param {Object} options.airportsMap - 机场映射
 * @param {Array} options.tableDataSource - 表格数据源
 * @param {Function} options.handleFlightInfoEditSuccess - 航班信息编辑成功回调
 * @param {Function} options.handleShowDetails - 显示航班详情回调
 * @returns {Array} 列定义数组
 */
export const getTimeColumns = ({ airportsMap, tableDataSource, handleFlightInfoEditSuccess }) => {
    // 动态计算轮挡时间 (Block Time) = EndTime - StartTime
    const calculateBlockTimeDifference = (startTime, endTime) => {
        if (!startTime || !endTime) return null;
        const diffMinutes = dayjs(endTime).diff(dayjs(startTime), "minute");
        if (diffMinutes === null || isNaN(diffMinutes)) return null;

        const hours = Math.floor(diffMinutes / 60);
        const mins = diffMinutes % 60;
        return hours > 0 ? `${hours}h${mins > 0 ? mins + "m" : ""}` : `${mins}m`;
    };

    // 通用的时间单元格渲染器（用 EnhancedFlightTimes 包裹）
    const renderTimeCell = (content, record) => {
        return <EnhancedFlightTimes flight={record} airportsMap={airportsMap} allFlights={tableDataSource} onFlightInfoEditSuccess={(flightId, responseData) => handleFlightInfoEditSuccess(flightId ?? record.id, responseData)} triggerComponent={<div style={{ width: "100%", height: "100%", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "inherit" }}>{content}</div>} />;
    };

    return [
        {
            title: "STD",
            dataIndex: "std",
            key: "std",
            align: "center",
            className: "compact-col-center monospaced-font compact-time-clickable",
            width: 36,
            render: (std, record) => {
                if (!std) return "-";
                const blockTimeStr = calculateBlockTimeDifference(record.std, record.sta);
                return renderTimeCell(<FlightTimeDisplay utcTime={std} airportCode={record.departure_airport} airportsMap={airportsMap} format="HH:mm" extraTooltipContent={blockTimeStr ? `计划轮挡: ${blockTimeStr}` : null} />, record);
            },
        },
        {
            title: "STA",
            dataIndex: "sta",
            key: "sta",
            align: "left",
            className: "compact-col-left monospaced-font compact-time-clickable",
            width: 40,
            render: (sta, record) => {
                if (!sta) return "-";
                const blockTimeStr = calculateBlockTimeDifference(record.std, record.sta);
                return renderTimeCell(<FlightTimeDisplay utcTime={sta} airportCode={record.arrival_airport} baseUtcTime={record.std} airportsMap={airportsMap} format="HH:mm" extraTooltipContent={blockTimeStr ? `计划轮挡: ${blockTimeStr}` : null} />, record);
            },
        },
        {
            title: "ETD",
            dataIndex: "etd",
            key: "etd",
            align: "left",
            className: "compact-col-left monospaced-font compact-time-clickable",
            width: 50,
            render: (etd, record) => {
                if (!etd) return "-";
                const diff = calculateTimeDiff(etd, record.std);
                const blockTimeStr = calculateBlockTimeDifference(record.etd, record.eta);
                return renderTimeCell(
                    <Space size={0}>
                        <FlightTimeDisplay utcTime={etd} airportCode={record.departure_airport} airportsMap={airportsMap} format="HH:mm" extraTooltipContent={blockTimeStr ? `预计轮挡: ${blockTimeStr}` : null} />
                        {diff !== 0 && diff !== null && (
                            <Tag color={getDelayTagColor(diff)} style={{ fontSize: "10px", lineHeight: "14px", height: "16px", padding: "0 3px", marginLeft: 2 }}>
                                {diff > 0 ? `+${diff}` : diff}
                            </Tag>
                        )}
                    </Space>,
                    record,
                );
            },
        },
        {
            title: "ETA",
            dataIndex: "eta",
            key: "eta",
            align: "left",
            className: "compact-col-left monospaced-font compact-time-clickable",
            width: 56,
            render: (eta, record) => {
                if (!eta) return "-";
                const diff = calculateTimeDiff(eta, record.sta);
                const blockTimeStr = calculateBlockTimeDifference(record.etd, record.eta);
                return renderTimeCell(
                    <Space size={0}>
                        <FlightTimeDisplay utcTime={eta} airportCode={record.arrival_airport} baseUtcTime={record.etd || record.std} airportsMap={airportsMap} format="HH:mm" extraTooltipContent={blockTimeStr ? `预计轮挡: ${blockTimeStr}` : null} />
                        {diff !== 0 && diff !== null && (
                            <Tag color={getDelayTagColor(diff)} style={{ fontSize: "10px", lineHeight: "14px", height: "16px", padding: "0 3px", marginLeft: 2 }}>
                                {diff > 0 ? `+${diff}` : diff}
                            </Tag>
                        )}
                    </Space>,
                    record,
                );
            },
        },
        {
            title: "ATD",
            dataIndex: "atd",
            key: "atd",
            align: "center",
            className: "compact-col-center monospaced-font compact-time-clickable",
            width: 58,
            render: (_, record) => {
                const resolved = resolveTime(record, "atd");
                if (!resolved.value) return "-";
                const resolvedAta = resolveTime(record, "ata");
                const blockTimeStr = calculateBlockTimeDifference(resolved.value, resolvedAta.value);
                const isSettlement = resolved.source === "settlement";
                return renderTimeCell(
                    <Space size={0} style={{ whiteSpace: "nowrap" }}>
                        <FlightTimeDisplay utcTime={resolved.value} airportCode={record.departure_airport} airportsMap={airportsMap} format="HH:mm" extraTooltipContent={blockTimeStr ? `实际轮挡: ${blockTimeStr}` : null} />
                        <Tag color={isSettlement ? "blue" : "default"} style={{ fontSize: "8px", lineHeight: "12px", height: "14px", padding: "0 2px", marginLeft: 1 }}>{isSettlement ? "结" : "运"}</Tag>
                    </Space>,
                    record,
                );
            },
        },
        {
            title: "ATA",
            dataIndex: "ata",
            key: "ata",
            align: "left",
            className: "compact-col-left monospaced-font compact-time-clickable",
            width: 62,
            render: (_, record) => {
                const resolved = resolveTime(record, "ata");
                if (!resolved.value) return "-";
                const resolvedAtd = resolveTime(record, "atd");
                const blockTimeStr = calculateBlockTimeDifference(resolvedAtd.value, resolved.value);
                const isSettlement = resolved.source === "settlement";
                return renderTimeCell(
                    <Space size={0} style={{ whiteSpace: "nowrap" }}>
                        <FlightTimeDisplay utcTime={resolved.value} airportCode={record.arrival_airport} baseUtcTime={resolvedAtd.value || record.std} airportsMap={airportsMap} format="HH:mm" extraTooltipContent={blockTimeStr ? `实际轮挡: ${blockTimeStr}` : null} />
                        <Tag color={isSettlement ? "blue" : "default"} style={{ fontSize: "8px", lineHeight: "12px", height: "14px", padding: "0 2px", marginLeft: 1 }}>{isSettlement ? "结" : "运"}</Tag>
                    </Space>,
                    record,
                );
            },
        },
        {
            title: <Tooltip title="过站时间">GT</Tooltip>,
            dataIndex: "actual_ground_time_minutes",
            key: "ground_time",
            align: "center",
            className: "compact-col-center monospaced-font",
            width: 36,
            render: (groundTime, record) => {
                if (!groundTime && groundTime !== 0) return "-";
                const hours = Math.floor(groundTime / 60);
                const minutes = groundTime % 60;

                // 如果过站时间小于最小要求，显示警告色
                const isWarning = record.min_ground_time_minutes && groundTime < record.min_ground_time_minutes;
                const color = isWarning ? "#faad14" : "inherit";

                // 使用 Flex 布局对齐
                // [小时(右对齐)][h][分钟(左对齐)]
                // 使用 inline-flex 让整体在单元格内居中（配合 align: center）
                return (
                    <Tooltip title={record.min_ground_time_minutes ? `最小过站: ${Math.floor(record.min_ground_time_minutes / 60)}h${record.min_ground_time_minutes % 60}m` : "过站时间"}>
                        <div style={{ display: "inline-flex", alignItems: "baseline", color, whiteSpace: "nowrap" }}>
                            {/* 小时部分：固定宽度，右对齐。26px支持3位数 */}
                            <span style={{ width: "26px", textAlign: "right", marginRight: "1px" }}>{hours > 0 ? hours : ""}</span>

                            {/* h 单位：仅当有小时的时候显示，或者占位？为了H对齐，位置要留着 */}
                            <span style={{ width: "8px", textAlign: "center" }}>{hours > 0 ? "h" : ""}</span>

                            {/* 分钟部分：左对齐 */}
                            <span style={{ width: "24px", textAlign: "left", marginLeft: "1px" }}>{minutes > 0 || hours === 0 ? `${minutes}m` : ""}</span>
                        </div>
                    </Tooltip>
                );
            },
        },
    ];
};

export default getTimeColumns;
